/**
 * Cliente de la API v2 de GoHighLevel (LeadConnector).
 *
 * Nota sobre versionado: el portal de documentacion se publica bajo la ruta
 * `/docs/2023-02-21/ghl`, pero el header HTTP `Version` que espera el API es
 * `2021-07-28`. Se configura en GHL_API_VERSION del .env del servidor.
 *
 * ---------------------------------------------------------------------------
 * Limites reales del API, medidos contra una subcuenta de 78.366 contactos y
 * 20.867 oportunidades (no estan documentados):
 *
 *   - GET /opportunities/search devuelve HTTP 500 al pasar de 10.000 registros
 *     en una misma busqueda, aunque se pagine por cursor.
 *   - POST /contacts/search muere con un 504 de Cloudflare alrededor de los
 *     20.000: la busqueda se encarece segun avanza el cursor.
 *
 * Por eso ninguna ventana larga se pide de una sola vez: se trocea en meses y
 * cada trozo se recorre con su propio cursor sobre un rango de fechas cerrado,
 * que es lo unico que mantiene cada busqueda pequena. Los parametros de rango
 * tambien estan verificados contra el API real:
 *
 *   - GET  /opportunities/search  ->  &date=<epoch ms>&endDate=<epoch ms>
 *     Solo epoch: ISO y 'YYYY-MM-DD' devuelven SEARCH_INVALID_START_DATE.
 *   - POST /contacts/search       ->  filters: [{ field: 'dateAdded',
 *                                       operator: 'range', value: { gte, lt } }]
 *
 * Ambos devuelven los registros de mas nuevo a mas viejo y paginan por cursor
 * (`startAfter`/`startAfterId` y `searchAfter`), no por numero de pagina.
 */

export const GHL_DIRECT_BASE = 'https://services.leadconnectorhq.com'
export const DEFAULT_API_VERSION = '2021-07-28'

/** Ventanas mas largas que esto se trocean en meses antes de pedirlas. */
const DIAS_SIN_TROCEAR = 100

/**
 * Peticiones simultaneas. GHL permite 100 cada 10s por subcuenta (10/s); con
 * 5 en vuelo y ~0.6s por peticion se queda algo por debajo.
 *
 * El limite se aplica a la peticion, no al trozo: el paralelismo por meses no
 * sirve de nada cuando un solo mes concentra el 98% de los registros, que es
 * exactamente el caso de esta subcuenta.
 */
const CONCURRENCIA = 5

/**
 * Registros por encima de los cuales un trozo se parte antes de pedirlo. Por
 * debajo del tope de 10.000 de oportunidades, con margen.
 */
const LIMITE_TROZO = 4000

/** No se parte por debajo de un minuto ni mas alla de esta profundidad. */
const MIN_TROZO_MS = 60000
const MAX_PROFUNDIDAD = 10

/** Cuantas veces se puede partir un trozo por un fallo (no por volumen). */
const MAX_PARTIR_POR_ERROR = 2

/** Coste medio medido de una peticion paginada, para estimar duraciones. */
export const MS_POR_PETICION = 600

/** Si no se sabe desde cuando hay datos, no se busca mas atras de aqui. */
const SUELO = Date.parse('2015-01-01T00:00:00Z')

export class GhlError extends Error {
  constructor(message, { status, path, payload } = {}) {
    super(message)
    this.name = 'GhlError'
    this.status = status
    this.path = path
    this.payload = payload
  }
}

export function createGhlClient({
  mode = 'proxy',
  token = '',
  version = DEFAULT_API_VERSION,
  proxyBase = '/api/ghl',
  concurrencia = CONCURRENCIA,
} = {}) {
  const base = mode === 'direct' ? GHL_DIRECT_BASE : proxyBase
  const permiso = crearSemaforo(concurrencia)

  async function request(path, { method = 'GET', query, body, signal, reintentos = 3 } = {}) {
    const qs = query ? '?' + new URLSearchParams(clean(query)).toString() : ''
    const headers = { Accept: 'application/json', Version: version }
    if (token) headers.Authorization = `Bearer ${token}`
    if (body) headers['Content-Type'] = 'application/json'

    for (let intento = 0; ; intento += 1) {
      const res = await permiso(() => fetch(base + path + qs, {
        method,
        headers,
        body: body ? JSON.stringify(body) : undefined,
        signal,
      }))

      const text = await res.text()
      let payload
      try { payload = text ? JSON.parse(text) : null } catch { payload = { raw: text } }

      if (res.ok) return payload

      // 429 (limite de peticiones) y 5xx (el gateway se satura en barridos
      // largos) son transitorios: se espera y se reintenta.
      if ((res.status === 429 || res.status >= 500) && intento < reintentos) {
        const espera = Number(res.headers.get('retry-after')) * 1000 || 1500 * (intento + 1)
        await pausa(espera, signal)
        continue
      }

      const detail = payload?.message || payload?.error || payload?.raw || res.statusText
      throw new GhlError(describe(res.status, detail), { status: res.status, path, payload })
    }
  }

  /**
   * Recorre un trozo con su propio cursor hasta agotarlo.
   * Si se queda sin paginas antes de terminar lo dice (`truncado`): cortar en
   * silencio haria pasar por completo un barrido al que le faltan registros.
   */
  async function recorrer({ pedir, leerLote, leerCursor, signal, onLote, maxPaginas = 200 }) {
    let cursor
    let total = null
    let n = 0
    let truncado = false
    for (let pagina = 0; ; pagina += 1) {
      if (pagina >= maxPaginas) { truncado = true; break }
      const data = await pedir(cursor)
      const lote = leerLote(data)
      total = leerTotal(data) ?? total
      n += lote.length
      if (lote.length) onLote?.(lote)
      cursor = leerCursor(data, lote)
      if (lote.length < 100 || !cursor) break
      if (signal?.aborted) throw new DOMException('Abortado', 'AbortError')
    }
    return { n, total, truncado }
  }

  const api = {
    version,
    mode,
    request,

    /** GET /locations/{id} -> nombre real de la subcuenta para la cabecera. */
    async getLocation(locationId, opts = {}) {
      const data = await request(`/locations/${locationId}`, opts)
      return data?.location ?? null
    },

    /** GET /opportunities/pipelines */
    async getPipelines(locationId, opts = {}) {
      const data = await request('/opportunities/pipelines', { query: { locationId }, ...opts })
      return data?.pipelines ?? []
    },

    /**
     * Fecha del registro mas antiguo de la subcuenta, para saber donde empieza
     * el troceo cuando se pide el historico completo.
     */
    async primerRegistro(locationId, { signal } = {}) {
      try {
        const data = await request('/contacts/search', {
          method: 'POST',
          signal,
          body: {
            locationId,
            pageLimit: 1,
            filters: [],
            sort: [{ field: 'dateAdded', direction: 'asc' }],
          },
        })
        const fecha = Date.parse(data?.contacts?.[0]?.dateAdded ?? '')
        return Number.isNaN(fecha) ? SUELO : fecha
      } catch {
        return SUELO
      }
    },

    /**
     * GET /opportunities/search sobre una ventana, troceada si hace falta.
     * @param {number|null} since epoch ms; null = todo el historico.
     * @param {function} [onBatch] recibe cada lote segun llega, para que la
     *   vista pueda ir pintando resultados parciales en barridos largos.
     */
    async searchOpportunities(locationId, { since = null, onProgress, onBatch, signal, desdeSuelo } = {}) {
      return barrer({
        signal,
        since,
        desdeSuelo,
        entidad: 'oportunidades',
        onProgress,
        onBatch,
        idDe: (o) => o.id ?? o._id,
        contar: (desde, hasta) => api.contarOportunidades(locationId, desde, hasta, { signal }),
        trozo: (desde, hasta, onLote, maxPaginas) => recorrer({
          signal,
          onLote,
          maxPaginas,
          leerLote: (d) => d?.opportunities ?? [],
          leerCursor: (d, lote) => (lote.length && d?.meta?.startAfterId
            ? { startAfter: d.meta.startAfter, startAfterId: d.meta.startAfterId }
            : null),
          pedir: (cursor) => request('/opportunities/search', {
            signal,
            query: {
              location_id: locationId,
              limit: 100,
              date: desde ?? undefined,
              endDate: hasta ?? undefined,
              startAfter: cursor?.startAfter,
              startAfterId: cursor?.startAfterId,
            },
          }),
        }),
      })
    },

    /** POST /contacts/search sobre una ventana, troceada si hace falta. */
    async searchContacts(locationId, { since = null, onProgress, onBatch, signal, desdeSuelo } = {}) {
      return barrer({
        signal,
        since,
        desdeSuelo,
        entidad: 'contactos',
        onProgress,
        onBatch,
        idDe: (c) => c.id ?? c._id,
        contar: (desde, hasta) => api.contarContactos(locationId, desde, hasta, { signal }),
        trozo: (desde, hasta, onLote, maxPaginas) => recorrer({
          signal,
          onLote,
          maxPaginas,
          leerLote: (d) => d?.contacts ?? [],
          leerCursor: (d, lote) => lote[lote.length - 1]?.searchAfter ?? null,
          pedir: (cursor) => request('/contacts/search', {
            method: 'POST',
            signal,
            body: {
              locationId,
              pageLimit: 100,
              filters: filtroFecha(desde, hasta),
              sort: [{ field: 'dateAdded', direction: 'desc' }],
              ...(cursor ? { searchAfter: cursor } : {}),
            },
          }),
        }),
      })
    },

    async contarOportunidades(locationId, desde, hasta, { signal } = {}) {
      const d = await request('/opportunities/search', {
        signal,
        query: { location_id: locationId, limit: 1, date: desde ?? undefined, endDate: hasta ?? undefined },
      })
      return d?.meta?.total ?? 0
    },

    async contarContactos(locationId, desde, hasta, { signal } = {}) {
      const d = await request('/contacts/search', {
        method: 'POST',
        signal,
        body: { locationId, pageLimit: 1, filters: filtroFecha(desde, hasta) },
      })
      return d?.total ?? 0
    },

    /** Cuenta registros sin descargarlos, para estimar el coste de un rango. */
    async contarRango(locationId, since, { signal } = {}) {
      const [oportunidades, contactos] = await Promise.all([
        api.contarOportunidades(locationId, since, null, { signal }),
        api.contarContactos(locationId, since, null, { signal }),
      ])
      const peticiones = Math.ceil(oportunidades / 100) + Math.ceil(contactos / 100) + 2
      return {
        oportunidades,
        contactos,
        peticiones,
        segundos: Math.ceil((peticiones * MS_POR_PETICION) / 1000 / CONCURRENCIA),
      }
    },
  }

  /**
   * Ejecuta la busqueda.
   *
   * Una ventana corta y poco poblada se pide de una vez. Cualquier otra se
   * trocea en meses, y cada mes se mide antes de descargarlo: si tiene mas
   * registros de los que el API aguanta en una busqueda, se parte por la mitad
   * tantas veces como haga falta. Hace falta de verdad: en esta subcuenta hay
   * un mes con 20.524 oportunidades de una importacion, mas del doble del tope.
   */
  async function barrer({ since, desdeSuelo, entidad, contar, trozo, idDe, onProgress, onBatch, signal }) {
    const ahora = Date.now()
    const dias = since === null ? Infinity : (ahora - since) / 86400000

    // Un trozo reintentado reenvia lo que ya habia entregado, asi que el
    // recuento y lo que sale por onBatch se deduplican por id.
    const vistos = new Set()
    let cargados = 0
    const avisar = (total) => onProgress?.({ loaded: cargados, total, entity: entidad })
    const recibir = (lote, total) => {
      const nuevos = lote.filter((x) => {
        const id = idDe(x)
        if (!id || vistos.has(id)) return false
        vistos.add(id)
        return true
      })
      if (!nuevos.length) return
      cargados += nuevos.length
      onBatch?.(nuevos)
      avisar(total)
    }

    const total = await contar(since, null).catch(() => null)
    avisar(total)

    if (dias <= DIAS_SIN_TROCEAR && (total === null || total <= LIMITE_TROZO)) {
      const r = await trozo(since, null, (lote) => recibir(lote, total), paginasPara(total))
      avisar(total)
      return {
        cargados,
        total: total ?? cargados,
        completo: !r.truncado,
        trozos: 1,
        fallidos: r.truncado ? 1 : 0,
      }
    }

    let fallidos = 0

    const partir = async (desde, hasta, profundidad, porError) => {
      const medio = desde + Math.floor((hasta - desde) / 2)
      await Promise.all([
        descargar(desde, medio, profundidad + 1, porError),
        descargar(medio, hasta, profundidad + 1, porError),
      ])
    }

    /**
     * @param porError cuantas veces se ha partido ya por un fallo, no por
     *   volumen. Va limitado aparte: partir arregla un trozo demasiado grande,
     *   pero no un tramo que el API rechaza siempre, y ahi cada nivel duplica
     *   el trabajo. Sin este tope, un tramo irrecuperable genera miles de
     *   subtrozos y el barrido se queda colgado en vez de dar el fallo.
     */
    const descargar = async (desde, hasta, profundidad = 0, porError = 0) => {
      const n = await contar(desde, hasta).catch(() => null)
      if (n === 0) return

      const partible = hasta - desde > MIN_TROZO_MS && profundidad < MAX_PROFUNDIDAD
      if (n !== null && n > LIMITE_TROZO && partible) return partir(desde, hasta, profundidad, porError)

      try {
        const r = await trozo(desde, hasta, (lote) => recibir(lote, total), paginasPara(n))
        if (!r.truncado) return
        // Se quedo sin paginas: hay mas de lo que cabia, partir si sirve.
        if (partible && porError < MAX_PARTIR_POR_ERROR) {
          return partir(desde, hasta, profundidad, porError + 1)
        }
      } catch (err) {
        if (err.name === 'AbortError') throw err
        // Con volumen ya bajo, el fallo no es de tamano: partir no lo arregla.
        const puedeSerDeTamano = n === null || n > LIMITE_TROZO / 4
        if (partible && puedeSerDeTamano && porError < MAX_PARTIR_POR_ERROR) {
          return partir(desde, hasta, profundidad, porError + 1)
        }
      }
      // Hueco: se cuenta, el barrido continua y el resultado no sera completo.
      fallidos += 1
    }

    const inicio = since ?? desdeSuelo ?? SUELO
    const meses = trozosMensuales(inicio, ahora)
    // Van todos a la vez: el semaforo de `request` es el que marca el ritmo, y
    // los meses estan ordenados de reciente a antiguo, que es el orden en que
    // se atienden.
    await Promise.all(meses.map((m) => descargar(m.desde, m.hasta)))
    avisar(total)

    return {
      cargados,
      total: total ?? cargados,
      // Completo solo si no hubo huecos Y salio todo lo que el API decia tener.
      completo: fallidos === 0 && (total === null || cargados >= total),
      trozos: meses.length,
      fallidos,
    }
  }

  return api
}

/** Divide [inicio, fin] en trozos de un mes natural. */
export function trozosMensuales(inicio, fin) {
  const trozos = []
  const cursor = new Date(inicio)
  cursor.setUTCDate(1)
  cursor.setUTCHours(0, 0, 0, 0)

  while (cursor.getTime() <= fin) {
    const desde = cursor.getTime()
    cursor.setUTCMonth(cursor.getUTCMonth() + 1)
    trozos.push({ desde: Math.max(desde, inicio), hasta: Math.min(cursor.getTime(), fin + 86400000) })
  }
  // De mas reciente a mas antiguo: lo util aparece antes en pantalla.
  return trozos.reverse()
}

/** Deja pasar como mucho `n` peticiones a la vez; el resto espera en cola FIFO. */
function crearSemaforo(n) {
  let enVuelo = 0
  const cola = []
  const siguiente = () => {
    enVuelo -= 1
    const espera = cola.shift()
    if (espera) espera()
  }
  return async function permiso(fn) {
    if (enVuelo >= n) await new Promise((resolve) => cola.push(resolve))
    enVuelo += 1
    try {
      return await fn()
    } finally {
      siguiente()
    }
  }
}

/** Paginas necesarias para `n` registros, con margen para lo que entre a la vez. */
function paginasPara(n) {
  if (!n && n !== 0) return 200
  return Math.ceil(n / 100) + 5
}

function filtroFecha(desde, hasta) {
  const value = {}
  if (desde !== null && desde !== undefined) value.gte = desde
  if (hasta !== null && hasta !== undefined) value.lt = hasta
  return Object.keys(value).length ? [{ field: 'dateAdded', operator: 'range', value }] : []
}

function leerTotal(data) {
  return data?.meta?.total ?? data?.total ?? null
}

function pausa(ms, signal) {
  return new Promise((resolve, reject) => {
    const id = setTimeout(resolve, ms)
    signal?.addEventListener('abort', () => { clearTimeout(id); reject(new DOMException('Abortado', 'AbortError')) }, { once: true })
  })
}

function clean(obj) {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined && v !== null && v !== ''))
}

function describe(status, detail) {
  if (status === 401) return `401 no autorizado: el token del servidor es invalido o expiro (${detail})`
  if (status === 403) return `403 sin permisos: al token le faltan scopes (contacts.readonly / opportunities.readonly) (${detail})`
  if (status === 404) return `404 no encontrado: revisa el locationId (${detail})`
  if (status === 422) return `422 peticion invalida: ${detail}`
  if (status === 429) return `429 limite de peticiones de GoHighLevel alcanzado (${detail})`
  if (status === 504) return `504 el API de GoHighLevel tardo demasiado (${detail})`
  return `Error ${status}: ${detail}`
}
